/**
 * AltairAgent – Dubai Police 999 Emergency Assistant
 *
 * Handles: session config, location, image upload (server-side), photo capture,
 *          incident card overlay, session-end handling, conversation persistence,
 *          and transcript buffering.
 */

import { useEffect, useRef, useState, useCallback, memo } from 'react';
import { useAgentContext } from '../../contexts/AgentContext';
import { useTranscript } from '../../contexts/TranscriptContext';
import { AllPermissions } from '../../utils/permissions';
import { logger } from '../../utils/logger';
import { config } from '../../config/runtime-config';
import { getEmbeddedHandler } from '../../services/embedded-handler';
import { IMAGE_UPLOAD_REQUEST_EVENT } from '../../agents/nodes/ImageUploadAgent';
import { SHOW_INCIDENT_CARD_EVENT } from '../../agents/nodes/IncidentCardAgent';
import { SESSION_ENDED_EVENT } from '../../agents/nodes/ClosingAgent';
import './altair-agent.scss';

const BACKEND_URL = config.REACT_APP_BACKEND_URL;

interface AltairAgentProps {
  permissions?: AllPermissions | null;
  userData?: { username: string; mobileNumber: string } | null;
  voiceGender?: 'male' | 'female';
  onLogout?: () => void;
  videoStream?: MediaStream | null;
  embeddedMode?: boolean;
  initialAgent?: string;
  initialContext?: Record<string, any>;
  onFlowComplete?: (result: any) => void;
}

// ─── Create a conversation record on the backend ────────────────────────────
async function createBackendConversation(
  userId: string,
  language: string,
  userName?: string,
  userPhone?: string,
): Promise<string | null> {
  if (!BACKEND_URL) return null;
  try {
    const res = await fetch(`${BACKEND_URL}/api/conversations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        language,
        user_name: userName || null,
        user_phone: userPhone || null,
      }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    const convId = data.conversation?.id || data.id || null;
    if (convId) localStorage.setItem('dp_conversation_id', convId);
    return convId;
  } catch (err) {
    logger.warn('[AltairAgent] Failed to create backend conversation:', err);
    return null;
  }
}

// ─── Patch conversation with location ───────────────────────────────────────
async function patchConversationLocation(
  convId: string,
  lat: number,
  lon: number,
  address: string,
): Promise<void> {
  if (!BACKEND_URL || !convId) return;
  try {
    await fetch(`${BACKEND_URL}/api/conversations/${convId}/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ location_lat: lat, location_lon: lon, location_address: address }),
    });
  } catch { /* non-critical */ }
}

// ─── Upload images to backend server ────────────────────────────────────────
async function uploadImagesToServer(convId: string, files: File[]): Promise<string[]> {
  if (!BACKEND_URL || !convId || !files.length) return [];
  const formData = new FormData();
  for (const file of files) {
    formData.append('files', file);
  }
  try {
    const res = await fetch(`${BACKEND_URL}/api/conversations/${convId}/upload`, {
      method: 'POST',
      body: formData,
    });
    if (!res.ok) {
      logger.warn('[AltairAgent] Image upload failed:', res.status);
      return [];
    }
    const data = await res.json();
    return data.urls || [];
  } catch (err) {
    logger.error('[AltairAgent] Image upload error:', err);
    return [];
  }
}

function AltairAgentComponent({
  permissions,
  userData,
  voiceGender = 'male',
  onLogout,
  videoStream,
  embeddedMode = false,
  initialAgent,
  initialContext,
  onFlowComplete,
}: AltairAgentProps) {
  const {
    connected,
    currentAgentId,
    context,
    isTransitioning,
    setConfig,
    send,
    sendRealtimeInput,
    updateTransitionData,
    transitionTo,
    client,
    manager,
    disconnect,
  } = useAgentContext();

  const { addMessage, setConversationId } = useTranscript();
  const embeddedHandler = embeddedMode ? getEmbeddedHandler(true) : null;

  // ── UI state ───────────────────────────────────────────────────────────────
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [stagePreviews, setStagePreviews] = useState<string[]>([]);
  const [incidentCard, setIncidentCard] = useState<{ number: string; visible: boolean }>({
    number: '',
    visible: false,
  });
  const photoCaptureRef = useRef<HTMLVideoElement>(null);
  const photoCameraStreamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Transcript buffering ───────────────────────────────────────────────────
  const aiBufferRef = useRef<string>('');
  const agentIdAtTurnStartRef = useRef<string | null>(null);
  const userBufferRef = useRef<string>('');

  const detectedLanguage = context?.transitionData?.language || 'en';

  // ── Session config ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!userData) return;
    logger.connection('[AltairAgent] Configuring session for:', userData.username);
    const configData: any = { userData, voiceGender, userLocation: undefined };
    if (initialContext) Object.assign(configData, initialContext);
    setConfig(configData);
  }, [userData, voiceGender, setConfig, initialContext]);

  // ── Create backend conversation on first connect ───────────────────────────
  useEffect(() => {
    if (!connected) return;

    const savedData = localStorage.getItem('dp_user_data');
    let userId = 'anonymous';
    let language = 'en';
    let userName: string | undefined;
    let userPhone: string | undefined;

    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        userId   = parsed.userId   || userId;
        language = parsed.language || language;
        const first = parsed.firstName || '';
        const last  = parsed.lastName  || '';
        const full  = `${first} ${last}`.trim();
        if (full) userName = full;
        if (parsed.phone) userPhone = parsed.phone;
      } catch {}
    }

    if (!userName && userData) userName = userData.username || undefined;

    createBackendConversation(userId, language, userName, userPhone).then((convId) => {
      if (convId) {
        setConversationId(convId);
        logger.connection('[AltairAgent] Conversation created:', convId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  // ── Embedded mode handlers ─────────────────────────────────────────────────
  useEffect(() => {
    if (!embeddedHandler) return;
    embeddedHandler.onLaunch((agent: string, ctx?: Record<string, any>) => {
      if (ctx) updateTransitionData(ctx);
      if (connected) transitionTo(agent, 'embedded_launch', ctx);
    });
    embeddedHandler.onContextUpdate((ctx: Record<string, any>) => updateTransitionData(ctx));
    embeddedHandler.onClose(() => {});
  }, [embeddedHandler, connected, transitionTo, updateTransitionData]);

  // ── Flow completion → notify parent ───────────────────────────────────────
  useEffect(() => {
    if (!manager || !embeddedHandler) return;
    const handleFlowComplete = (flowType: string, result: any, agentId: string) => {
      embeddedHandler.sendResult(result, agentId);
      onFlowComplete?.(result);
    };
    manager.on('flowCompleted', handleFlowComplete);
    return () => { manager.off('flowCompleted', handleFlowComplete); };
  }, [manager, embeddedHandler, onFlowComplete]);

  // ── Deep-link to initial agent ─────────────────────────────────────────────
  useEffect(() => {
    if (connected && initialAgent && initialAgent !== 'greeting_agent' && currentAgentId === 'greeting_agent') {
      const timer = setTimeout(() => transitionTo(initialAgent, 'deep_link', initialContext), 500);
      return () => clearTimeout(timer);
    }
  }, [connected, initialAgent, currentAgentId, initialContext, transitionTo]);

  // ── Embedded status heartbeat ──────────────────────────────────────────────
  useEffect(() => {
    if (!embeddedHandler) return;
    embeddedHandler.sendStatus('idle', currentAgentId || undefined);
  }, [embeddedHandler, connected, isTransitioning, currentAgentId]);

  // ── Location detection ─────────────────────────────────────────────────────
  // Only run when the session first becomes connected; re-running on every
  // connected change (e.g. agent transitions) would cause duplicate requests.
  const locationFetchedRef = useRef(false);
  useEffect(() => {
    if (!permissions?.location || !navigator.geolocation || !connected) return;
    // Fetch location only once per session (guard against effect re-fires on
    // agent transitions where connected briefly flips false → true).
    if (locationFetchedRef.current) return;
    locationFetchedRef.current = true;

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        try {
          const res = await fetch(
            `${config.REACT_APP_NOMINATIM_API_URL}/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=ar`
          );
          const data = await res.json();
          const a = data.address || {};
          const parts = [a.road, a.suburb || a.neighbourhood, a.city || a.town, a.state, a.country].filter(Boolean);
          const arabicAddress = parts.length > 0 ? parts.join('، ') : data.display_name;
          // Store in transition data — this propagates to every future agent's
          // system instruction via inject_context_into_instruction on the backend.
          // Do NOT call send() here: the greeting agent uses a constrained
          // WebSocket endpoint that does not support clientContent messages,
          // causing an immediate 1008 close if a text send is attempted.
          updateTransitionData({ userLocation: { lat, lng, address: arabicAddress } });

          const convId = localStorage.getItem('dp_conversation_id');
          if (convId) patchConversationLocation(convId, lat, lng, arabicAddress);
        } catch (err) {
          logger.error('[AltairAgent] Geocoding error:', err);
        }
      },
      (err) => logger.error('[AltairAgent] Location error:', err)
    );
  }, [permissions, connected, updateTransitionData]);

  // ── Listen for image upload request event (from InquiryAgent) ─────────────
  useEffect(() => {
    const handleImageUploadRequest = () => {
      logger.info('[AltairAgent] Image upload requested by inquiry agent');
      setShowImageUpload(true);
    };
    window.addEventListener(IMAGE_UPLOAD_REQUEST_EVENT, handleImageUploadRequest);
    return () => window.removeEventListener(IMAGE_UPLOAD_REQUEST_EVENT, handleImageUploadRequest);
  }, []);

  // ── Listen for incident card event (from ClosingAgent) ────────────────────
  useEffect(() => {
    const handleShowIncidentCard = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const number = detail?.incidentNumber || localStorage.getItem('dp_incident_number') || 'N/A';
      logger.info('[AltairAgent] Showing incident card – number:', number);
      setIncidentCard({ number, visible: true });
    };
    window.addEventListener(SHOW_INCIDENT_CARD_EVENT, handleShowIncidentCard);
    return () => window.removeEventListener(SHOW_INCIDENT_CARD_EVENT, handleShowIncidentCard);
  }, []);

  // ── Listen for session ended event (from ClosingAgent) ────────────────────
  useEffect(() => {
    const handleSessionEnded = () => {
      logger.info('[AltairAgent] Session ended by user');
      // Gracefully disconnect after a short delay to let the goodbye message play
      setTimeout(() => {
        if (disconnect) disconnect();
      }, 2000);
    };
    window.addEventListener(SESSION_ENDED_EVENT, handleSessionEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, handleSessionEnded);
  }, [disconnect]);

  // ── Reset upload UI when agent changes ────────────────────────────────────
  useEffect(() => {
    setShowImageUpload(false);
    stopPhotoCamera();
    setShowPhotoCapture(false);
    // Revoke any staged preview URLs to avoid memory leaks
    setStagePreviews(prev => { prev.forEach(url => URL.revokeObjectURL(url)); return []; });
    setStagedFiles([]);
  }, [currentAgentId]);

  // ── Transcript recording (buffered – message-by-message) ──────────────────
  useEffect(() => {
    if (!client) return;

    const handleUserSpeechChunk = (text: string) => {
      if (text) userBufferRef.current += text;
    };

    const flushUserBuffer = () => {
      const text = userBufferRef.current.trim();
      if (text) addMessage({ role: 'user', content: text });
      userBufferRef.current = '';
    };

    const handleAISpeechChunk = (text: string) => {
      if (text) {
        if (!aiBufferRef.current) {
          flushUserBuffer();
          agentIdAtTurnStartRef.current = currentAgentId || null;
        }
        aiBufferRef.current += text;
      }
    };

    const flushAIBuffer = () => {
      const text = aiBufferRef.current.trim();
      if (text) {
        addMessage({
          role: 'assistant',
          content: text,
          agentId: agentIdAtTurnStartRef.current || undefined,
        });
      }
      aiBufferRef.current = '';
      agentIdAtTurnStartRef.current = null;
      flushUserBuffer();
    };

    const handleInterrupted = () => { flushAIBuffer(); };

    client.on('inputTranscription', handleUserSpeechChunk);
    client.on('outputTranscription', handleAISpeechChunk);
    client.on('turncomplete', flushAIBuffer);
    client.on('interrupted', handleInterrupted);

    return () => {
      client.off('inputTranscription', handleUserSpeechChunk);
      client.off('outputTranscription', handleAISpeechChunk);
      client.off('turncomplete', flushAIBuffer);
      client.off('interrupted', handleInterrupted);
    };
  }, [client, addMessage, currentAgentId]);

  // ── Photo camera helpers ───────────────────────────────────────────────────
  const stopPhotoCamera = useCallback(() => {
    if (photoCameraStreamRef.current) {
      photoCameraStreamRef.current.getTracks().forEach(t => t.stop());
      photoCameraStreamRef.current = null;
    }
  }, []);

  const startPhotoCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      photoCameraStreamRef.current = stream;
      if (photoCaptureRef.current) {
        photoCaptureRef.current.srcObject = stream;
      }
      setShowPhotoCapture(true);
    } catch (err) {
      logger.error('[AltairAgent] Camera access failed:', err);
    }
  }, []);

  // ── Stage files for preview (does NOT upload yet) ─────────────────────────
  const stageFiles = useCallback((newFiles: File[]) => {
    if (!newFiles.length) return;
    const newPreviews = newFiles.map(f => URL.createObjectURL(f));
    setStagedFiles(prev => [...prev, ...newFiles]);
    setStagePreviews(prev => [...prev, ...newPreviews]);
  }, []);

  const capturePhoto = useCallback(() => {
    const video = photoCaptureRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx2d = canvas.getContext('2d');
    if (!ctx2d) return;
    ctx2d.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      stopPhotoCamera();
      setShowPhotoCapture(false);
      // Add to staged — don't close main overlay, let user add more or finish
      stageFiles([file]);
    }, 'image/jpeg', 0.92);
  }, [stopPhotoCamera, stageFiles]);

  // ── Finish upload: send staged files to backend then transition directly ──
  const handleFinishUpload = useCallback(async () => {
    const filesToUpload = [...stagedFiles];

    // Revoke preview URLs and clear state immediately
    stagePreviews.forEach(url => URL.revokeObjectURL(url));
    setStagedFiles([]);
    setStagePreviews([]);
    setShowImageUpload(false);

    if (filesToUpload.length && connected) {
      const convId = localStorage.getItem('dp_conversation_id');
      if (convId) {
        const urls = await uploadImagesToServer(convId, filesToUpload);
        logger.info('[AltairAgent] Images uploaded to server:', urls);
      } else {
        logger.warn('[AltairAgent] No conversation_id — images not saved');
      }
    }

    // Transition directly to agency_agent — bypass LLM to avoid race conditions
    const currentData = context?.transitionData || {};
    logger.info('[AltairAgent] Image upload done — transitioning to agency_agent');
    transitionTo('agency_agent', 'image_upload_complete', currentData);
  }, [stagedFiles, stagePreviews, connected, context, transitionTo]);

  // ── File picker handler — stages files, does not upload ───────────────────
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length) stageFiles(files);
    e.target.value = '';
  }, [stageFiles]);

  // ── Incident card OK button ────────────────────────────────────────────────
  const handleIncidentCardOk = useCallback(() => {
    setIncidentCard(prev => ({ ...prev, visible: false }));
    // Signal the LLM so it calls proceed_to_closing (also interrupts any ongoing speech)
    send('OK', true);
  }, [send]);

  // ── Label helpers for multilingual UI ─────────────────────────────────────
  const lang = detectedLanguage as 'ar' | 'en' | 'ur';
  const labels = {
    uploadTitle:    lang === 'ar' ? 'هل لديك صور؟' : lang === 'ur' ? 'کیا آپ کے پاس تصاویر ہیں؟' : 'Do you have photos?',
    uploadTitleHas: lang === 'ar' ? 'الصور المختارة' : lang === 'ur' ? 'منتخب تصاویر' : 'Selected Images',
    uploadBtn:      lang === 'ar' ? 'رفع صورة' : lang === 'ur' ? 'تصویر اپلوڈ کریں' : 'Upload Image',
    uploadMoreBtn:  lang === 'ar' ? 'إضافة المزيد' : lang === 'ur' ? 'مزید شامل کریں' : 'Add More',
    photoBtn:       lang === 'ar' ? 'التقاط صورة' : lang === 'ur' ? 'تصویر لیں' : 'Live Camera',
    finishBtn:      lang === 'ar' ? 'إنهاء' : lang === 'ur' ? 'مکمل کریں' : 'Finish',
    skipBtn:        lang === 'ar' ? 'تخطي' : lang === 'ur' ? 'چھوڑیں' : 'Skip',
    captureBtn:     lang === 'ar' ? 'التقاط' : lang === 'ur' ? 'کھینچیں' : 'Capture',
    cancelBtn:      lang === 'ar' ? 'إلغاء' : lang === 'ur' ? 'منسوخ' : 'Cancel',
    incidentTitle:  lang === 'ar' ? 'تم تسجيل بلاغك' : lang === 'ur' ? 'آپ کی رپورٹ درج ہوگئی' : 'Your Report Has Been Filed',
    incidentLabel:  lang === 'ar' ? 'رقم البلاغ' : lang === 'ur' ? 'واقعہ نمبر' : 'Incident Number',
    incidentOk:     lang === 'ar' ? 'حسناً' : lang === 'ur' ? 'ٹھیک ہے' : 'OK',
  };

  return (
    <div className="altair-agent">

      {/* ─── Image Upload Overlay ──────────────────────────────────────────── */}
      {showImageUpload && !showPhotoCapture && (
        <div className="upload-overlay">
          <div className="upload-card" onClick={e => e.stopPropagation()}>

            {stagedFiles.length === 0 ? (
              /* ── Phase 1: Ask if user wants to upload ── */
              <>
                <h3 className="upload-title">{labels.uploadTitle}</h3>
                <div className="upload-actions">
                  <label className="upload-btn primary">
                    <span className="material-symbols-outlined">photo_library</span>
                    {labels.uploadBtn}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFileInputChange}
                    />
                  </label>
                  <button className="upload-btn secondary" onClick={startPhotoCamera}>
                    <span className="material-symbols-outlined">photo_camera</span>
                    {labels.photoBtn}
                  </button>
                </div>
                <button className="upload-skip" onClick={() => {
                  setShowImageUpload(false);
                  const currentData = context?.transitionData || {};
                  logger.info('[AltairAgent] Image upload skipped — transitioning to agency_agent');
                  transitionTo('agency_agent', 'image_upload_skipped', currentData);
                }}>
                  {labels.skipBtn}
                </button>
              </>
            ) : (
              /* ── Phase 2: Preview staged images + action buttons ── */
              <>
                <h3 className="upload-title">{labels.uploadTitleHas} ({stagedFiles.length})</h3>

                <div className="upload-preview-grid">
                  {stagePreviews.map((src, idx) => (
                    <div key={idx} className="upload-preview-item">
                      <img src={src} alt={`preview-${idx}`} className="upload-preview-img" />
                      <button
                        className="upload-preview-remove"
                        title="Remove"
                        onClick={() => {
                          URL.revokeObjectURL(src);
                          setStagedFiles(prev => prev.filter((_, i) => i !== idx));
                          setStagePreviews(prev => prev.filter((_, i) => i !== idx));
                        }}
                      >
                        <span className="material-symbols-outlined">close</span>
                      </button>
                    </div>
                  ))}
                </div>

                <div className="upload-actions">
                  <label className="upload-btn secondary">
                    <span className="material-symbols-outlined">add_photo_alternate</span>
                    {labels.uploadMoreBtn}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFileInputChange}
                    />
                  </label>
                  <button className="upload-btn secondary" onClick={startPhotoCamera}>
                    <span className="material-symbols-outlined">photo_camera</span>
                    {labels.photoBtn}
                  </button>
                </div>

                <button className="upload-btn primary upload-finish-btn" onClick={handleFinishUpload}>
                  <span className="material-symbols-outlined">check_circle</span>
                  {labels.finishBtn}
                </button>
              </>
            )}

          </div>
        </div>
      )}

      {/* ─── Live Photo Capture ────────────────────────────────────────────── */}
      {showPhotoCapture && (
        <div className="photo-capture-overlay">
          <video
            ref={photoCaptureRef}
            autoPlay
            playsInline
            muted
            className="photo-preview"
            onCanPlay={() => photoCaptureRef.current?.play()}
          />
          <div className="photo-capture-actions">
            <button className="capture-btn" onClick={capturePhoto}>
              <span className="material-symbols-outlined">camera</span>
              {labels.captureBtn}
            </button>
            <button className="cancel-btn" onClick={() => { stopPhotoCamera(); setShowPhotoCapture(false); }}>
              {labels.cancelBtn}
            </button>
          </div>
        </div>
      )}

      {/* ─── Incident Number Card ──────────────────────────────────────────── */}
      {incidentCard.visible && (
        <div className="incident-card-overlay">
          <div className="incident-card">
            <div className="incident-card-header">
              <img src="/emirate_police.png" alt="Dubai Police" className="incident-card-logo" />
              <span>{labels.incidentTitle}</span>
            </div>
            <div className="incident-card-body">
              <p className="incident-card-label">{labels.incidentLabel}</p>
              <div className="incident-card-number-row">
                <p className="incident-card-number">{incidentCard.number}</p>
                <button
                  className="incident-card-copy-btn"
                  title={lang === 'ar' ? 'نسخ الرقم' : lang === 'ur' ? 'نمبر کاپی کریں' : 'Copy number'}
                  onClick={() => navigator.clipboard.writeText(incidentCard.number)}
                >
                  <span className="material-symbols-outlined">content_copy</span>
                </button>
              </div>
            </div>
            <button className="incident-card-ok-btn" onClick={handleIncidentCardOk}>
              {labels.incidentOk}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export const AltairAgent = memo(AltairAgentComponent);
export default AltairAgent;
